import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Link as LinkIcon, CheckCircle, Loader2, Search } from 'lucide-react';

import { API } from '@/lib/apiBase';

export default function LinkStudent() {
  const { getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [student, setStudent] = useState(null);
  const [linked, setLinked] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      toast.error(t('linkStudent.enterCode'));
      return;
    }

    setLoading(true);
    setStudent(null);
    setLinked(false);

    try {
      const headers = { ...getAuthHeaders() };
      const response = await fetch(`${API}/students/code/${code.toUpperCase()}`, {
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        toast.error(response.status === 404 ? t('linkStudent.notFound') : t('linkStudent.searchFailed'));
        return;
      }

      setStudent(await response.json());
    } catch (error) {
      toast.error(t('linkStudent.searchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleLink = async () => {
    if (!student) return;

    setLoading(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      
      const response = await fetch(`${API}/students/${student.student_id}/link-parent`, {
        method: 'POST',
        headers,
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to link');

      setLinked(true);
      toast.success(t('linkStudent.linkSuccess'));
    } catch (error) {
      toast.error(t('linkStudent.linkFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-6 md:px-12 py-12" data-testid="link-student-page">
      <Card className="bg-card border-border">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <LinkIcon className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl font-bold">Link Your Child</CardTitle>
          <CardDescription>
            Enter the unique code provided by your school to see your child&apos;s photos
          </CardDescription>
        </CardHeader>

        <CardContent>
          {!linked ? (
            <>
              <form onSubmit={handleSearch} className="space-y-4 mb-6">
                <div className="space-y-2">
                  <Label htmlFor="code">Student Code</Label>
                  <div className="flex gap-2">
                    <Input
                      id="code"
                      placeholder="e.g., SC1A2B3C4D"
                      value={code}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                      className="h-12 bg-background border-border font-mono uppercase tracking-wider"
                      data-testid="student-code-input"
                    />
                    <Button
                      type="submit"
                      disabled={loading}
                      className="h-12 px-6 bg-primary hover:bg-primary/90"
                      data-testid="search-student-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </form>

              {student && (
                <div className="border border-border rounded-sm p-4 mb-4">
                  <h3 className="font-semibold mb-2">Student Found</h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-muted-foreground">Name:</span> <span className="font-medium">{student.name}</span></p>
                    <p><span className="text-muted-foreground">Code:</span> <span className="font-mono">{student.unique_code}</span></p>
                  </div>

                  <Button
                    onClick={handleLink}
                    disabled={loading}
                    className="w-full mt-4 bg-primary hover:bg-primary/90"
                    data-testid="link-student-btn"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Linking...</>
                    ) : (
                      <><LinkIcon className="w-4 h-4 mr-2" />Link This Child</>
                    )}
                  </Button>
                </div>
              )}

              <p className="text-xs text-muted-foreground text-center">
                The code should have been provided by your school via letter or email.
              </p>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Successfully Linked!</h3>
              <p className="text-muted-foreground mb-6">
                {student?.name} has been linked to your account. You can now view their photos in the gallery.
              </p>
              <Button
                onClick={() => { setCode(''); setStudent(null); setLinked(false); }}
                variant="outline"
              >
                Link Another Child
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
